import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import type {
  ConversationPlan,
  ConversationSummary,
  InformationNeed,
  QuantitativeResponse,
  Sentiment,
  SupportingQuote,
} from '@knownow/domain'
import { SupabaseConversationRepository } from '@knownow/conversation'
import { Badge, Button } from '@knownow/ui'
import { supabase } from '@/lib/supabase'
import {
  generateConversationSummary,
  getConversationSummary,
} from '@/lib/summary-api-client'
import { QuantitativeResponseDisplay } from './quantitative-response-display'

export interface ConversationSummaryPanelProps {
  readonly plan: ConversationPlan
  readonly conversationId: string
  readonly organizationId: string
  readonly canSummarize: boolean
}

type Status = 'idle' | 'loading' | 'generating' | 'ready' | 'error'

interface PanelState {
  status: Status
  summary: ConversationSummary | null
  responses: readonly QuantitativeResponse[]
  error: string | null
}

export function ConversationSummaryPanel({
  plan,
  conversationId,
  organizationId,
  canSummarize,
}: ConversationSummaryPanelProps): React.ReactElement {
  const [state, setState] = useState<PanelState>({
    status: 'loading',
    summary: null,
    responses: [],
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    const repo = new SupabaseConversationRepository(supabase, organizationId)
    Promise.all([
      getConversationSummary(conversationId).catch(() => null),
      repo.listQuantitativeResponsesByConversation(conversationId).catch(
        () => [] as readonly QuantitativeResponse[],
      ),
    ]).then(([summary, responses]) => {
      if (cancelled) return
      setState({
        status: summary ? 'ready' : 'idle',
        summary,
        responses,
        error: null,
      })
    })
    return (): void => {
      cancelled = true
    }
  }, [conversationId, organizationId])

  const runGenerate = useCallback(async () => {
    setState((s) => ({ ...s, status: 'generating', error: null }))
    try {
      const summary = await generateConversationSummary(conversationId)
      setState((s) => ({ ...s, status: 'ready', summary, error: null }))
    } catch (err) {
      setState((s) => ({
        ...s,
        status: 'error',
        error: (err as Error).message,
      }))
    }
  }, [conversationId])

  const activeNeeds = plan.informationNeeds.filter((n) => n.deletedAt === null)

  return (
    <div className="flex flex-col h-full" data-testid="conversation-summary-panel">
      <PanelHeader
        summary={state.summary}
        status={state.status}
        canSummarize={canSummarize}
        onRun={runGenerate}
      />
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {state.status === 'loading' && (
          <p className="text-sm text-neutral-500">Loading summary…</p>
        )}
        {state.status === 'idle' && (
          <IdleState canSummarize={canSummarize} onRun={runGenerate} />
        )}
        {state.status === 'generating' && <GeneratingSkeleton needs={activeNeeds} />}
        {state.status === 'error' && (
          <ErrorState message={state.error ?? 'Something went wrong'} onRetry={runGenerate} />
        )}
        {state.status === 'ready' && state.summary && (
          <SummaryView
            summary={state.summary}
            needs={activeNeeds}
            responses={state.responses}
          />
        )}
      </div>
    </div>
  )
}

function PanelHeader({
  summary,
  status,
  canSummarize,
  onRun,
}: {
  summary: ConversationSummary | null
  status: Status
  canSummarize: boolean
  onRun: () => void
}): React.ReactElement {
  const busy = status === 'generating'
  const label = summary ? 'Regenerate' : 'Summarize'
  return (
    <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
      <h3 className="text-sm font-semibold text-neutral-800">
        Findings
      </h3>
      <Button
        variant="secondary"
        size="sm"
        onClick={onRun}
        loading={busy}
        disabled={!canSummarize || busy}
      >
        {label}
      </Button>
    </div>
  )
}

function IdleState({
  canSummarize,
  onRun,
}: {
  canSummarize: boolean
  onRun: () => void
}): React.ReactElement {
  if (!canSummarize) {
    return (
      <p className="text-sm text-neutral-500" data-testid="summary-disabled">
        Summaries are available after the interview is marked complete.
      </p>
    )
  }
  return (
    <div className="rounded-lg border border-dashed border-neutral-200 p-6 text-center">
      <p className="text-sm text-neutral-600 mb-3">
        Generate a structured analysis of this conversation, grouped by
        information need.
      </p>
      <Button variant="primary" size="sm" onClick={onRun}>
        Summarize
      </Button>
    </div>
  )
}

function GeneratingSkeleton({
  needs,
}: {
  needs: readonly InformationNeed[]
}): React.ReactElement {
  return (
    <div className="space-y-3" data-testid="summary-generating">
      <div className="h-16 rounded-lg bg-neutral-100 animate-pulse" />
      {needs.map((n) => (
        <div
          key={n.id}
          className="h-24 rounded-lg bg-neutral-100 animate-pulse"
        />
      ))}
    </div>
  )
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}): React.ReactElement {
  return (
    <div
      className="rounded-lg bg-error-light text-error-dark p-4 text-sm"
      data-testid="summary-error"
    >
      <p className="mb-2">Summary failed: {message}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

function SummaryView({
  summary,
  needs,
  responses,
}: {
  summary: ConversationSummary
  needs: readonly InformationNeed[]
  responses: readonly QuantitativeResponse[]
}): React.ReactElement {
  const findingByNeedId = new Map(
    summary.findings.map((f) => [f.informationNeedId, f]),
  )
  const responsesByNeedId = new Map<string, QuantitativeResponse[]>()
  for (const r of responses) {
    const list = responsesByNeedId.get(r.informationNeedId) ?? []
    list.push(r)
    responsesByNeedId.set(r.informationNeedId, list)
  }

  return (
    <div className="space-y-4">
      <section
        className="rounded-lg border border-neutral-200 bg-neutral-0 p-4"
        data-testid="summary-narrative"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">
          Overview
        </h4>
        <p className="text-sm text-neutral-800 leading-relaxed">
          {summary.narrative}
        </p>
      </section>

      {needs.map((need) => (
        <NeedCard
          key={need.id}
          need={need}
          finding={findingByNeedId.get(need.id) ?? null}
          responses={responsesByNeedId.get(need.id) ?? []}
        />
      ))}

      <p className="text-xs text-neutral-400">
        Generated {summary.generatedAt.toLocaleString()} · model{' '}
        {summary.modelVersion}
      </p>
    </div>
  )
}

function NeedCard({
  need,
  finding,
  responses,
}: {
  need: InformationNeed
  finding:
    | {
        readonly qualitativeSummary: string | null
        readonly supportingQuotes: readonly SupportingQuote[]
      }
    | null
  responses: readonly QuantitativeResponse[]
}): React.ReactElement {
  const priorityVariant =
    need.priority === 'high'
      ? 'error'
      : need.priority === 'low'
        ? 'neutral'
        : 'warning'
  return (
    <article
      className="rounded-lg border border-neutral-200 bg-neutral-0 p-4"
      data-testid={`need-card-${need.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h4 className="text-sm font-medium text-neutral-800 leading-snug">
          {need.description}
        </h4>
        <div className="flex gap-1 shrink-0">
          <Badge variant={priorityVariant} size="sm">
            {need.priority}
          </Badge>
          <Badge variant="info" size="sm">
            {need.collectionFormat}
          </Badge>
        </div>
      </div>

      {finding?.qualitativeSummary && (
        <p className="text-sm text-neutral-700 leading-relaxed mb-3">
          {finding.qualitativeSummary}
        </p>
      )}

      {finding && finding.supportingQuotes.length > 0 && (
        <ul className="space-y-2 mb-3">
          {finding.supportingQuotes.map((q, i) => (
            <li
              key={i}
              className="border-l-2 border-brand-300 ps-3"
              data-testid={`supporting-quote-${need.id}-${i}`}
            >
              <p className="font-serif text-sm text-neutral-800 italic leading-relaxed">
                &ldquo;{q.content}&rdquo;
              </p>
              {q.sentiment && (
                <span className="mt-1 inline-block">
                  <Badge variant={sentimentVariant(q.sentiment)} size="sm">
                    {q.sentiment}
                  </Badge>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {responses.length > 0 && (
        <div className="rounded-md bg-neutral-50 p-3">
          <p className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
            Respondent answer
          </p>
          {responses.map((r) => (
            <QuantitativeResponseDisplay key={r.id} need={need} response={r} />
          ))}
        </div>
      )}

      {!finding?.qualitativeSummary &&
        (finding?.supportingQuotes.length ?? 0) === 0 &&
        responses.length === 0 && (
          <p className="text-sm text-neutral-400 italic">
            Not addressed in this conversation.
          </p>
        )}
    </article>
  )
}

function sentimentVariant(
  sentiment: Sentiment,
): 'success' | 'error' | 'warning' | 'neutral' {
  switch (sentiment) {
    case 'positive':
      return 'success'
    case 'negative':
      return 'error'
    case 'mixed':
      return 'warning'
    case 'neutral':
      return 'neutral'
  }
}
